import { authLoginError, authLoginSuccess, authMfaCancel, authMfaRequired } from '@store/slices/auth/authSlice.js';
import { getFormBodyEncoded, logoutClearStore } from '@utils/functions';
import httpClientLogin from '@utils/httpClientLogin';
import { clearTrustedDeviceToken, getTrustedDeviceToken, setToken, setTrustedDeviceToken, setUser } from '@utils/tokenStorage';
import { call, put } from 'redux-saga/effects';

// Credentials of a login that stopped at the MFA step. Module memory only — never in the
// store (devtools) and never in storage. Holds the password or, for social login, the hash.
let pendingCredentials = null;

// /Token errors that keep the user on the MFA step instead of dropping back to the login form.
const MFA_STEP_ERRORS = ['mfa_required', 'mfa_invalid_code', 'mfa_locked'];

function loginApiCall({ email, password, isGoogle = false, isFacebook = false, isTelegram = false, isTikTok = false, mfaCode, trustDevice = false }) {
  const deviceToken = getTrustedDeviceToken();

  return httpClientLogin.post(
    `token`,
    getFormBodyEncoded({
      Username: email,
      Password: password,
      grant_type: 'password',
      ...(isGoogle && { google: true }),
      ...(isFacebook && { facebook: true }),
      ...(isTelegram && { telegram: true }),
      ...(isTikTok && { tiktok: true }),
      ...(deviceToken && { device_token: deviceToken }),
      ...(mfaCode && { mfa_code: mfaCode }),
      ...(mfaCode && trustDevice && { trust_device: true }),
    }),
  );
}

function* completeLogin(loginData) {
  const token = loginData.access_token;
  const user = loginData.userName;

  if (loginData.trusted_device_token) {
    setTrustedDeviceToken(loginData.trusted_device_token);
  }

  setToken(token);
  setUser(user);

  yield put(authLoginSuccess({ token, email: user }));
}

export function* loginRequestSaga({ payload }) {
  const { email, password, isGoogle = false, isFacebook = false, isTelegram = false, isTikTok = false } = payload;
  pendingCredentials = null;
  try {
    const { data: loginData } = yield call(loginApiCall, { email, password, isGoogle, isFacebook, isTelegram, isTikTok });

    yield* completeLogin(loginData);
  } catch (error) {
    const data = error?.response?.data;

    if (MFA_STEP_ERRORS.includes(data?.error)) {
      pendingCredentials = { email, password, isGoogle, isFacebook, isTelegram, isTikTok };
      // The server checks the trusted device before asking for a code, so mfa_required means
      // the stored token is expired or was removed in settings — stop sending it.
      if (data.error === 'mfa_required') {
        clearTrustedDeviceToken();
      }
      yield put(authMfaRequired(data.error === 'mfa_required' ? null : data.error_description));
    } else {
      yield put(authLoginError(data?.error_description ? data?.error_description : error?.toString()));
    }

    logoutClearStore();
  }
}

export function* mfaSubmitSaga({ payload }) {
  const { code, trustDevice } = payload;

  if (!pendingCredentials) {
    yield put(authMfaCancel());
    return;
  }

  try {
    const { data: loginData } = yield call(loginApiCall, { ...pendingCredentials, mfaCode: code, trustDevice });

    pendingCredentials = null;
    yield* completeLogin(loginData);
  } catch (error) {
    const data = error?.response?.data;

    if (MFA_STEP_ERRORS.includes(data?.error)) {
      yield put(authMfaRequired(data.error_description || data.error));
    } else {
      pendingCredentials = null;
      yield put(authMfaCancel());
      yield put(authLoginError(data?.error_description ? data?.error_description : error?.toString()));
      logoutClearStore();
    }
  }
}

export function mfaCancelSaga() {
  pendingCredentials = null;
}
